// @owners { team: patients }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { SCHEDULED_DOWNTIME_URL } from '~shared/config';

const FETCH_TIMEOUT_MS = 5000;

// Bump this (and update ScheduledDowntimePayload/isScheduledDowntimePayload for the new shape)
// when the schema changes — anything else is treated as unrecognized and fails open.
const CURRENT_SCHEMA_VERSION = 1;

export type ScheduledDowntimePayload = {
  schema_version: typeof CURRENT_SCHEMA_VERSION;
  title: string;
  message: string;
  starts_at: string;
  // Missing/null means an indefinite outage — see isDowntimeActive.
  expires_at?: string | null;
  phone_number?: string;
};

// Any shape we don't recognize — including a schema_version higher than we know how to
// interpret — is treated the same as a fetch error by the caller: fail open, no block shown.
const isScheduledDowntimePayload = (data: unknown): data is ScheduledDowntimePayload => {
  if (typeof data !== 'object' || data === null) return false;

  const payload = data as ScheduledDowntimePayload;
  return (
    payload.schema_version === CURRENT_SCHEMA_VERSION &&
    typeof payload.title === 'string' &&
    typeof payload.message === 'string' &&
    typeof payload.starts_at === 'string' &&
    (payload.expires_at === undefined || payload.expires_at === null || typeof payload.expires_at === 'string') &&
    (payload.phone_number === undefined || typeof payload.phone_number === 'string')
  );
};

const fetchScheduledDowntime = async (): Promise<ScheduledDowntimePayload> => {
  const controller = new AbortController();
  const timeoutID = setTimeout(() => {
    controller.abort();
  }, FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(SCHEDULED_DOWNTIME_URL, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Unexpected status fetching scheduled downtime: ${response.status}`);
    }

    const data: unknown = await response.json();
    if (!isScheduledDowntimePayload(data)) {
      throw new Error('Received malformed or unrecognized scheduled downtime payload');
    }

    return data;
  } finally {
    clearTimeout(timeoutID);
  }
};

export const scheduledDowntime = createQueryKeys('scheduledDowntime', {
  fetchStatus: {
    queryKey: null,
    queryFn: fetchScheduledDowntime,
  },
});
